import React, { useState, useMemo } from 'react';
import { Delete, Plus, SearchThick } from 'sushiweb/Icons';
import { Input, Modal } from '../../../../UiKit';
import Multiselect from 'multiselect-react-dropdown';
import { useMutationCall } from '../../../../api/react-client';
import {
  delayDiallersEndPoint,
  ETA_LIMITS,
  multiSelectStyles,
  SELECT_ALL,
  TEXT,
} from './constants';
import { useClient } from '../../../../api/api-client';
import toast from 'react-hot-toast';
import Button from '../../../../components/ComponentsV1/Button';

const EditDDModal = ({
  openEditDiallersModal,
  setOpenEditDiallersModal,
  KarmaScores,
  refetchDelayDiallers,
  selectedOrg = {},
}) => {
  const client = useClient();
  // State for karma sections, each with karma selection and ETA buffer
  const [karmaSections, setKarmaSections] = useState([
    {
      id: Date.now(),
      selectedKarmas: [],
      etaBuffer: 5,
    },
  ]);
  const [expiryTime, setExpiryTime] = useState('');

  // Get all selected karma IDs across all sections
  const allSelectedKarmaIds = useMemo(() => {
    const selectedIds = new Set();

    karmaSections.forEach((section) => {
      if (section.selectedKarmas) {
        section.selectedKarmas.forEach((karma) => {
          selectedIds.add(karma.id);
        });
      }
    });

    return selectedIds;
  }, [karmaSections]);

  // Check if all karma options have been selected
  const allOptionsSelected = useMemo(() => {
    return allSelectedKarmaIds.size >= Object.keys(KarmaScores).length;
  }, [allSelectedKarmaIds, KarmaScores]);

  // Check if last section has no karma selected
  const lastSectionEmpty = useMemo(() => {
    const lastSection = karmaSections[karmaSections.length - 1];
    return (
      !lastSection.selectedKarmas || lastSection.selectedKarmas.length === 0
    );
  }, [karmaSections]);

  // Determine if add button should be disabled
  const isAddButtonDisabled = allOptionsSelected || lastSectionEmpty;

  // Add a new karma section
  const addKarmaSection = () => {
    if (lastSectionEmpty) {
      toast.error(TEXT.SELECT_ATLEAST_ONE_SECTION);
      return;
    }
    if (allOptionsSelected) {
      toast.error(TEXT.ALL_OPTIONS_SELECTED);
      return;
    }
    setKarmaSections([
      ...karmaSections,
      { id: Date.now(), selectedKarmas: [], etaBuffer: 5 },
    ]);
  };

  // Remove a karma section
  const removeKarmaSection = (indexToRemove) => {
    setKarmaSections(
      karmaSections.filter((_, index) => index !== indexToRemove),
    );
  };

  // Update selected karmas for a section
  const handleKarmaSelect = (selectedList, sectionIndex, selectedItem) => {
    // Check if "Select All" was clicked
    if (selectedItem?.id === SELECT_ALL) {
      const availableOptions = getFilteredOptions(sectionIndex).filter(
        (option) => option.id !== SELECT_ALL,
      );

      const updatedSections = [...karmaSections];
      updatedSections[sectionIndex].selectedKarmas = availableOptions;
      setKarmaSections(updatedSections);
      return;
    }

    const filteredList = selectedList.filter((item) => item.id !== SELECT_ALL);

    const updatedSections = [...karmaSections];
    updatedSections[sectionIndex].selectedKarmas = filteredList;
    setKarmaSections(updatedSections);
  };

  // Update ETA buffer for a section
  const handleEtaChange = (value, sectionIndex) => {
    const updatedSections = [...karmaSections];
    updatedSections[sectionIndex].etaBuffer = value;
    setKarmaSections(updatedSections);
  };

  // Get all selected karma IDs except for the current section
  const getSelectedKarmaIds = (currentSectionIndex) => {
    const selectedIds = new Set();

    karmaSections.forEach((section, index) => {
      // Skip the current section when collecting selected IDs
      if (index !== currentSectionIndex && section.selectedKarmas) {
        section.selectedKarmas.forEach((karma) => {
          selectedIds.add(karma.id);
        });
      }
    });

    return selectedIds;
  };

  // Get filtered options for a specific section
  const getFilteredOptions = (sectionIndex) => {
    const selectedIds = getSelectedKarmaIds(sectionIndex);

    const filteredOptions = Object.keys(KarmaScores)
      .filter((key) => !selectedIds.has(key))
      .map((key) => ({
        id: key,
        name: KarmaScores[key],
      }));

    // Only add "Select All" if there are options and not all are already selected
    const currentSelection = karmaSections[sectionIndex]?.selectedKarmas || [];
    const allSelectableOptions = filteredOptions.length;
    const currentlySelected = currentSelection.length;

    // If there are options to select and not all available options are already selected
    if (
      filteredOptions.length > 0 &&
      currentlySelected < allSelectableOptions
    ) {
      return [{ id: SELECT_ALL, name: 'Select All' }, ...filteredOptions];
    }

    return filteredOptions;
  };

  //mutation for setting delay diallers
  const { mutate: setDelayDiallers, isLoading: isSettingDelayDiallers } =
    useMutationCall(
      {
        onSuccess: (res) => {
          if (res?.success === false) {
            toast.error(res?.message ?? 'Something went wrong');
          } else {
            toast.success(res?.message ?? 'Delay diallers set successfully');
          }
          refetchDelayDiallers();
          setOpenEditDiallersModal(false);
        },
      },
      delayDiallersEndPoint,
      ['set_delay_dialler', selectedOrg?.id],
      {
        doNotShowSuccessToast: true,
      },
      (useData) =>
        client(delayDiallersEndPoint, {
          customOrgId: selectedOrg?.id,
          data: useData,
        }),
    );

  const handleSave = () => {
    const data = {
      expiry_ts: expiryTime,
      delay_diallers: {},
    };

    karmaSections.forEach((section) => {
      section.selectedKarmas.forEach((karma) => {
        data.delay_diallers[karma.id] = {
          eta_buffer_in_mins: section.etaBuffer,
        };
      });
    });

    // check for valid eta values
    const invalidEta = Object.values(data.delay_diallers).some(
      (dialler) =>
        dialler.eta_buffer_in_mins < ETA_LIMITS.MIN ||
        dialler.eta_buffer_in_mins > ETA_LIMITS.MAX,
    );

    if (invalidEta) {
      toast.error(TEXT.EXPIRY_TIME_BTW_5_AND_30);
      return;
    }

    setDelayDiallers(data);
  };

  return (
    <Modal
      isVisible={openEditDiallersModal}
      padding={0}
      paddingTop={'1rem'}
      type='large'
      height='100%'
      maxHeight='80vh'
      containerStyles={{ margin: '16px 0 20px 0', padding: '0 20px' }}
      titleComponent={
        <span className='tw-text-[1.8rem] tw-font-medium tw-text-zinc-600'>
          Edit Delay Diallers for{' '}
          <span className='tw-text-[1.8rem] tw-font-bold tw-text-zinc-600'>
            {selectedOrg?.name}
          </span>
        </span>
      }
      handleClose={() => setOpenEditDiallersModal(false)}
    >
      <div className='tw-flex tw-flex-col tw-gap-6 tw-m-0'>
        <div className='tw-flex tw-flex-col tw-w-full tw-gap-12'>
          {karmaSections.map((section, sectionIndex) => (
            <div
              key={section?.id}
              className='tw-w-full tw-flex tw-gap-4 tw-items-center'
            >
              <div className='tw-flex tw-flex-col tw-w-full tw-gap-8 tw-items-center tw-border tw-border-opacity-5 tw-border-solid tw-border-[#404040] tw-rounded-[0.6rem] tw-p-6 tw-bg-[#f9f9f9]'>
                <div className='tw-w-full tw-h-full' css={multiSelectStyles}>
                  <div className='tw-font-medium tw-text-[#434343] tw-text-[16px] tw-mb-4'>
                    Karma Scores
                  </div>
                  <Multiselect
                    className='tw-w-40 tw-bg-white'
                    avoidHighlightFirstOption
                    customArrow={<SearchThick />}
                    loading={false}
                    placeholder={`Search or Select Karma`}
                    options={getFilteredOptions(sectionIndex)}
                    onSelect={(selectedList, selectedItem) => {
                      handleKarmaSelect(
                        selectedList,
                        sectionIndex,
                        selectedItem,
                      );
                    }}
                    onRemove={(selectedList, selectedItem) => {
                      handleKarmaSelect(
                        selectedList,
                        sectionIndex,
                        selectedItem,
                      );
                    }}
                    displayValue='name'
                    selectedValues={section.selectedKarmas}
                  />
                </div>
                <div className='tw-flex tw-items-center tw-gap-2 tw-w-full tw-justify-between'>
                  <div className='tw-w-full'>
                    <div className='tw-font-medium tw-text-[#434343] tw-text-[16px] tw-mb-4'>
                      ETA Buffer (in mins)
                    </div>
                    <div className='tw-flex tw-h-20 tw-items-center tw-gap-2'>
                      <Input
                        placeholder='Enter the ETA Buffer (in mins)'
                        type='number'
                        showMaxLimit={false}
                        min={5}
                        max={30}
                        value={section.etaBuffer}
                        onChange={(value) =>
                          handleEtaChange(Number(value), sectionIndex)
                        }
                        inputStyleProps={{
                          height: '100%',
                          border: '1px solid #D1D5DB',
                          borderRadius: '0.6rem',
                          minWidth: '300px',
                        }}
                      />
                    </div>
                    <div className='tw-mt-4 tw-text-grey-700 tw-text-[12px]'>
                      {TEXT.EXPIRY_TIME_BTW_5_AND_30}
                    </div>
                  </div>
                </div>
              </div>
              <div
                className={`tw-aspect-square tw-h-20 ${
                  karmaSections.length === 1 ? 'tw-hidden' : ''
                }`}
              >
                <button
                  className=' tw-cursor-pointer tw-aspect-square tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-[0.6rem] tw-bg-red-100  hover:tw-bg-[#ffe3e6] tw-transition-all tw-duration-200 tw-ease-in-out tw-border tw-border-solid tw-border-opacity-5 border-solid tw-border-[#ff5f5f]'
                  disabled={karmaSections.length === 1}
                  onClick={() => removeKarmaSection(sectionIndex)}
                >
                  <Delete
                    size={16}
                    color={'#ff5f5f'}
                    className='hover:tw-scale-110 tw-transition-all tw-duration-200 tw-ease-in-out active:tw-scale-95'
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className='tw-h-20 tw-full tw-flex tw-items-center tw-justify-end'>
          <button
            className='tw-w-full tw-cursor-pointer tw-rounded-[0.6rem] tw-h-full tw-flex tw-items-center tw-justify-center tw-bg-[#eeffe9] tw-font-medium tw-text-[#318616] tw-border tw-border-opacity-5 tw-border-solid tw-border-[#318616] hover:tw-bg-[#e9ffe2] tw-transition-all tw-duration-200 tw-ease-in-out'
            onClick={addKarmaSection}
          >
            <Plus
              size={20}
              disabled={isAddButtonDisabled}
              color={'#318616'}
              className={
                'hover:tw-scale-110 tw-transition-all tw-duration-200 tw-ease-in-out active:tw-scale-95 '
              }
            />
            <span className='tw-text-[16px] tw-ml-2'>Add Rule</span>
          </button>
        </div>
      </div>

      {allOptionsSelected && (
        <div className='tw-text-red-500 tw-text-sm tw-text-center tw-mt-6'>
          {TEXT.ALL_OPTIONS_SELECTED}
        </div>
      )}

      <div className='tw-mt-12'>
        <div className='tw-font-medium tw-text-[#434343] tw-text-[16px] tw-mb-4'>
          Expiry time
        </div>
        <Input
          type='datetime-local'
          showMaxLimit={false}
          value={expiryTime}
          onChange={(value) => setExpiryTime(value)}
          inputStyleProps={{
            height: '100%',
            border: '1px solid #D1D5DB',
            borderRadius: '0.6rem',
            width: '30%',
          }}
        />
      </div>

      <div className='tw-mt-6 tw-w-full tw-flex tw-justify-end'>
        <Button
          onClick={handleSave}
          disabled={!expiryTime || lastSectionEmpty}
          loading={isSettingDelayDiallers}
          title={`Update Delay Diallers for ${selectedOrg?.name}`}
        />
      </div>
    </Modal>
  );
};

export default EditDDModal;
